import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, ArrowRight, Sparkles, Lock, Loader2, Eye, EyeOff, ArrowUp } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { CardWatchMark } from "@/components/CardWatchLogo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.9 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.4 19 12 24 12c3 0 5.9 1.1 8 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29 35.4 26.6 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2c-.4.4 6.5-4.7 6.5-14.8 0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);

const Signup = () => {
  const [accepted, setAccepted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [mode, setMode] = useState("register"); // 'register' | 'login'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handlePasswordKeyEvent = (e) => {
    try {
      setCapsLockOn(e.getModifierState && e.getModifierState("CapsLock"));
    } catch {
      /* browsers without getModifierState — ignore */
    }
  };
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const startGoogleAuth = () => {
    // Only enforce the consent checkbox on the Sign-up tab.
    // Existing users on the Sign-in tab already accepted at registration
    // and the checkbox is intentionally hidden for them.
    if (mode === "register" && !accepted) {
      toast.error("Please tick the Privacy Policy box to continue");
      return;
    }
    setRedirecting(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    // Persist consent so the callback can forward it to the backend (registrations only)
    if (mode === "register") {
      try { sessionStorage.setItem("cardwatch_privacy_accepted", "1"); } catch {}
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitEmailAuth = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (!accepted) {
        toast.error("Please tick the Privacy Policy box to continue");
        return;
      }
      if (password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
    }
    setSubmitting(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload =
        mode === "register"
          ? { email: email.trim(), password, name: name.trim() || null, accepted_privacy: accepted }
          : { email: email.trim(), password };
      const res = await axios.post(`${API}${path}`, payload, { withCredentials: true });
      await refresh();
      const firstTime = res.data?.first_time || !res.data?.user?.onboarded;
      navigate(firstTime ? "/onboarding" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Couldn't sign you in"));
    } finally {
      setSubmitting(false);
    }
  };

  const isLogin = mode === "login";

  return (
    <div className="min-h-screen bg-[#060B19] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[160px]" />

      <header className="relative z-10 border-b border-white/5">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="signup-logo-link">
            <CardWatchMark size={30} />
            <span
              className="text-white font-semibold tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}
            >
              Card<span style={{ color: "#00E676" }}>Watch</span>
            </span>
          </Link>
          <button
            onClick={() => navigate("/demo")}
            data-testid="signup-view-demo"
            className="text-sm text-slate-400 hover:text-white transition-colors"
          >
            Try demo without signing up →
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
            <Sparkles size={11} /> Free · Takes 5 seconds
          </div>
          <h1
            className="mt-6 text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="signup-title"
          >
            Sign up for <span className="text-[#00E676]">CardWatch</span>.
          </h1>
          <p className="mt-5 text-slate-300 text-[15px] leading-relaxed">
            One private dashboard for every credit card you own. Bank-grade
            open banking, reminders 2 days before every due date, and utilization
            insights that keep your credit score healthy.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {[
              "Track balances across every card and issuer",
              "Daily payment-due email reminders",
              "Bank-grade, read-only linking via Plaid",
              "Your data is encrypted at rest and in transit",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <ShieldCheck size={16} className="mt-0.5 text-[#00E676] flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="rounded-3xl border border-white/8 bg-[#0B1226]/70 backdrop-blur-xl p-8 lg:p-10"
          data-testid="signup-card"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#00E676]/[0.08] border border-[#00E676]/25 flex items-center justify-center">
              <Lock size={18} className="text-[#00E676]" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
                {isLogin ? "Sign in" : "Sign up"}
              </div>
              <h2
                className="text-white text-2xl font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {isLogin ? "Welcome back" : "Create your account"}
              </h2>
            </div>
          </div>

          {/* Mode toggle */}
          <div
            className="mt-6 inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1 text-xs"
            role="tablist"
            data-testid="signup-mode-toggle"
          >
            {[
              { key: "register", label: "Create account" },
              { key: "login", label: "Sign in" },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setMode(t.key)}
                data-testid={`signup-mode-${t.key}`}
                className={`px-4 py-1.5 rounded-full font-semibold transition-colors ${
                  mode === t.key
                    ? "bg-[#00E676] text-[#060B19]"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {!isLogin && (
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              One-tap with Google or use email &amp; password — either works.
            </p>
          )}

          <label
            className={`mt-5 flex items-start gap-3 cursor-pointer group ${
              isLogin ? "hidden" : ""
            }`}
            data-testid="signup-privacy-checkbox-label"
          >
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="signup-privacy-checkbox"
              className="mt-1 h-4 w-4 rounded border border-white/20 bg-transparent accent-[#00E676] cursor-pointer flex-shrink-0"
            />
            <span className="text-sm text-slate-300 leading-relaxed">
              I have read and agree to the{" "}
              <Link
                to="/privacy"
                target="_blank"
                className="text-[#00E676] hover:underline"
                data-testid="signup-privacy-inline-link"
              >
                Privacy Policy
              </Link>{" "}
              and consent to CardWatch processing my card data as described.
            </span>
          </label>

          <button
            onClick={startGoogleAuth}
            disabled={(!isLogin && !accepted) || redirecting}
            data-testid="signup-google-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white text-[#0E172C] font-semibold px-6 py-3.5 text-sm transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            <GoogleG size={18} />
            {redirecting ? "Redirecting…" : `Continue with Google`}
            {!redirecting && <ArrowRight size={15} />}
          </button>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            <div className="flex-1 h-px bg-white/8" />
            or with email
            <div className="flex-1 h-px bg-white/8" />
          </div>

          <form onSubmit={submitEmailAuth} className="space-y-3" data-testid="signup-email-form">
            {!isLogin && (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                data-testid="signup-name-input"
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676]/60"
              />
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              data-testid="signup-email-input"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676]/60"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handlePasswordKeyEvent}
                onKeyUp={handlePasswordKeyEvent}
                onBlur={() => setCapsLockOn(false)}
                placeholder={isLogin ? "Your password" : "8+ characters"}
                autoComplete={isLogin ? "current-password" : "new-password"}
                minLength={isLogin ? undefined : 8}
                required
                data-testid="signup-password-input"
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 pr-12 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676]/60"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="signup-password-toggle"
                tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-300 hover:text-white transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-[#00E676]/40"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {capsLockOn && (
              <div
                className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-300"
                role="alert"
                data-testid="password-capslock-warning"
              >
                <ArrowUp size={11} strokeWidth={2.4} />
                Caps Lock is on
              </div>
            )}
            {!isLogin && <PasswordStrengthMeter password={password} />}
            <button
              type="submit"
              disabled={submitting || (!isLogin && !accepted)}
              data-testid="signup-email-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] hover:bg-[#00E676]/[0.16] hover:border-[#00E676] text-[#00E676] font-semibold px-6 py-3 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <>
                  {isLogin ? "Sign in" : "Create account"}
                  <ArrowRight size={14} />
                </>
              )}
            </button>
            {isLogin && (
              <div className="pt-1 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-slate-400 hover:text-white transition-colors"
                  data-testid="signup-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-center text-xs text-slate-500" data-testid="signup-mode-hint">
            {isLogin ? (
              <>
                New here?{" "}
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  className="text-[#00E676] hover:underline"
                  data-testid="signup-switch-to-register"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-[#00E676] hover:underline"
                  data-testid="signup-switch-to-login"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </motion.div>
      </main>

      <footer className="relative z-10 border-t border-white/5">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <CardWatchMark size={22} />
            <span>© {new Date().getFullYear()} CardWatch</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/demo" className="hover:text-white transition-colors">Demo</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Signup;
